/* eslint-disable max-lines */
import React, { useState } from 'react';
import { Layout } from 'fronton-react';
import { PercentIcon } from '@fronton/icons-react';
import { useLocation } from '@okapi/router-react';
import { useStoreon } from 'storeon/react';
import {
  ButtonWithGAClickButton,
  ReviewsCatalog,
  ReviewsSection,
  RootLayout,
  SectionSubTitle,
  SignSection,
  Typography,
} from 'common/components';
import { CategoriesSection, KITCHEN_SIGN_SECTION_GALLERY_LIST, REVIEWS_DATA } from 'common/components/kitchen';
import {
  Pdp,
  ProductInfoNew,
  ProductMedia,
  ProductStickyPanel,
} from 'common/components/pdp';
import { KAZAKHSTAN } from 'common/constants/region-codes';
import { useEffectOnce } from 'common/hooks';
import { fullWidthMobileButton } from 'common/styles/common';
import { KitchenGallery } from 'feature/kitchen-gallery';
import { COLLECTIONS_STORE_NAME, selectCollectionInfo, VariantAxisPoint } from 'modules/collections';
import { MODEL_JSON_STORE_NAME, selectRegionId } from 'modules/model-json';
import { RelatedService } from 'modules/related-services';
import { COOKIES_STORE_NAME } from 'modules/cookies';
import { State } from 'store/types';
import { ExperimentCookie } from 'types/common';
import { analyticsUtils } from 'utils/analytics-utils';
import { removeSpecialCharacters } from 'utils/format';
import {
  getProductGAInfo,
  previewClickGAEvent,
  serviceClickGAEvent,
  serviceShowGAEvent,
} from 'utils';
import { AdditionalInfo, CollectionComposition, Stages } from './components';
import {
  KITCHEN_PRICE_PDP_ELEMENT_BLOCK,
  KITCHEN_PRICE_PDP_LANDING_NAME,
} from './kitchen-price-pdp-constants';
import {
  serviceDisclaimerWrapper,
  signSection,
  signSectionTitle,
} from './kitchen-price-pdp-styles';
import { addToCartGAEvent } from './kitchen-price-pdp-utils';

export const KitchenPricePdp = () => {
  const [path] = useLocation();
  const [showComposition, setShowComposition] = useState(false);

  const { collections } = useStoreon<State>(COLLECTIONS_STORE_NAME);
  const { cookies } = useStoreon<State>(COOKIES_STORE_NAME);
  const { modelJson } = useStoreon<State>(MODEL_JSON_STORE_NAME);

  const regionId = selectRegionId(modelJson);
  const collectionInfo = selectCollectionInfo(collections);

  const [activePoint, setActivePoint] = useState(collectionInfo?.variants?.activePoint || ({} as VariantAxisPoint));
  const experimentCookie = (cookies._proekt_kuhni || 'B') as ExperimentCookie;

  const isRuRegion = regionId !== KAZAKHSTAN;

  const basketHref = `/zapis-onlayn/?department_no=15&region_id=${regionId}`;

  const productName = removeSpecialCharacters(activePoint.displayedName);

  const productGAInfo = getProductGAInfo(activePoint);

  useEffectOnce(() => {
    if (activePoint) {
      analyticsUtils.sendEvent('GA:detail', {
        elementType: 'page',
        elementBlock: KITCHEN_PRICE_PDP_ELEMENT_BLOCK, // блок, в котором расположен элемент
        productName, // название комплекта
        productId: activePoint.productId, // идентификатор комплекта
        productPrice: activePoint.price.price, // показанная стоимость комплекта
        productPrevPrice: null, // стоимость единицы товара до снижения цены или null
        productBelong: 'LeroyMerlin', // принадлежность товара
        komplektyId: activePoint.productId, // идентификатор комплекта
        division: 'Кухни', // отдел, к которому относится товар
        subdivision: 'Подотдел кухонь', // подотдел, к которому относится товар
        abTestPrice: null,
      });
    }
  });

  const handleAddToCartClick = () => {
    addToCartGAEvent(productGAInfo, activePoint.displayedName, 'Записаться на проектирование');
  };

  const handleAddToCartStickyPanelClick = () => {
    addToCartGAEvent(productGAInfo, 'sticky-menu', 'Записаться');
  };

  const handleServiceShowEvent = (service: RelatedService, position: number) => {
    serviceShowGAEvent(activePoint, service, position, KITCHEN_PRICE_PDP_ELEMENT_BLOCK);
  };

  const handleServiceClick = (service: RelatedService, position: number) => {
    serviceClickGAEvent(activePoint, service, position, KITCHEN_PRICE_PDP_ELEMENT_BLOCK);
  };

  const handlePreviewClick = (index: number) => {
    previewClickGAEvent(activePoint, activePoint.mediaPhoto[index], KITCHEN_PRICE_PDP_ELEMENT_BLOCK);
  };

  const handleDisclaimerClick = () => {
    setShowComposition(true);
    analyticsUtils.clickButtonEvent(
      'komplektyDetail',
      'Цена за стандартную комплектацию 240 см',
      'productsInKomplekty'
    );
  }

  const handleCloseComposition = () => {
    setShowComposition(false);
  }

  const handleAxisPointClick = (point: VariantAxisPoint) => {
    setActivePoint({
      ...activePoint,
      mediaPhoto: point.mediaPhoto,
    });
  }

  return (
    <RootLayout currentPage={path}>
      <Pdp
        productMedia={
          <ProductMedia
            collectionInfo={activePoint}
            onPreviewClick={handlePreviewClick}
            landingName={KITCHEN_PRICE_PDP_LANDING_NAME}
          />
        }
        productInfo={
          <ProductInfoNew
            additionalInfo={<AdditionalInfo />}
            basketHref={basketHref}
            basketHrefTarget="_blank"
            buttonLabel="Записаться на проектирование"
            currency={isRuRegion ? '₽' : '₸'}
            disclaimer={'Цена за стандартную комплектацию 240 см'}
            experimentCookie={experimentCookie}
            onAxisPointClick={handleAxisPointClick}
            onDisclaimerClick={handleDisclaimerClick}
            onAddToCartClick={handleAddToCartClick}
            onServiceClick={handleServiceClick}
            onServiceShowEvent={handleServiceShowEvent}
            pricePrefix="от"
            serviceDisclaimer={
              <div className={serviceDisclaimerWrapper}>
                <PercentIcon color="service-dark" size="s" />
                <Typography variant="minor_caption" color="service-dark">
                  Компенсируем стоимость замера при заказе установки в&nbsp;магазине или онлайн
                </Typography>
              </div>
            }
            servicePrefixPrice="от"
            withCredit={isRuRegion}
            landingName={KITCHEN_PRICE_PDP_LANDING_NAME}
          />
        }>
        <ProductStickyPanel
          basketHref={basketHref}
          basketHrefTarget="_blank"
          buttonLabel="Записаться"
          currency={isRuRegion ? '₽' : '₸'}
          pricePrefix="от"
          collectionInfo={activePoint}
          isCollectionAvailable
          landingName={KITCHEN_PRICE_PDP_LANDING_NAME}
          onAddToCartClick={handleAddToCartStickyPanelClick}
        />
        <Layout>
          <CollectionComposition
            basketHref={basketHref}
            elementBlock={KITCHEN_PRICE_PDP_ELEMENT_BLOCK}
            onClose={handleCloseComposition}
            show={showComposition}
          />
          <Stages />
        </Layout>
        <SignSection
          className={signSection}
          elementBlock={KITCHEN_PRICE_PDP_ELEMENT_BLOCK}
          elementName="Приезжайте в гости — посмотрите кухни вживую"
          galleryList={KITCHEN_SIGN_SECTION_GALLERY_LIST}
          signHref="/zapis-onlayn/?department_no=15&region_id="
          renderTitle={() => (
            <SectionSubTitle className={signSectionTitle} margin="s" color="text-invert">
              Приезжайте в&nbsp;гости&nbsp;&mdash; посмотрите кухни вживую
            </SectionSubTitle>
          )}
        />
        <ReviewsSection
          description="Делаем кухни, которые хочется показывать"
          review={
            <ReviewsCatalog
              elementBlock={KITCHEN_PRICE_PDP_ELEMENT_BLOCK}
              newVariant
              reviewsList={REVIEWS_DATA}
              modalButton={
                <ButtonWithGAClickButton
                  className={fullWidthMobileButton}
                  elementBlock={`${KITCHEN_PRICE_PDP_ELEMENT_BLOCK} / Reviews`}
                  elementName="Отзывы клиентов"
                  elementText="Запишитесь"
                  title="Запишитесь на проектирование"
                  href={basketHref}
                  target="_blank"
                  variant="accent"
                  wide
                >
                  Записаться на проектирование
                </ButtonWithGAClickButton>
              }
            />
          }
        />
        <Layout>
          <CategoriesSection elementBlock={KITCHEN_PRICE_PDP_ELEMENT_BLOCK} />
          <KitchenGallery
            elementBlock={KITCHEN_PRICE_PDP_ELEMENT_BLOCK}
            isRuRegion={isRuRegion}
            title="Другие кухни"
          />
        </Layout>
      </Pdp>
    </RootLayout>
  );
};
